import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Users, 
  Layers,
  ShieldCheck, 
  Plus, 
  Activity, 
  Server
} from 'lucide-react';
import api from './services/api';
import { useAuth } from './context/AuthContext';
import LeaveDashboard from './components/LeaveDashboard';
import StaffList from './components/StaffList';
import LeaveModal from './components/LeaveModal';
import Login from './components/Login';
import TenantPolicies from './components/TenantPolicies';

export default function App() {
  const { user, tenantId, token, logout } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [activeTab, setActiveTab] = useState('all');
  const [systemHealth, setSystemHealth] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newLeave, setNewLeave] = useState({ title: '', startDate: '', endDate: '', type: 'conge_annuel' });

  // Fetch holidays & system health
  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [holidaysRes, healthRes] = await Promise.allSettled([
        api.get(`/tenants/${tenantId}/holidays`),
        api.get('/healthz')
      ]);

      if (holidaysRes.status === 'fulfilled') {
        setHolidays(holidaysRes.value.data.data || []);
      }

      if (healthRes.status === 'fulfilled') {
        setSystemHealth(healthRes.value.data);
      }
    } catch (err) {
      console.error('Data fetch error', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [tenantId, token]);

  // If no token, render Login portal
  if (!token) {
    return <Login />;
  }

  // Wait for user to be populated by AuthContext initAuth
  if (!user) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--text-muted)' }}>Loading session...</div>;
  }

  const handleCreateLeave = async (e) => {
    e.preventDefault();
    if (!newLeave.title || !newLeave.startDate || !newLeave.endDate) return;

    try {
      await api.post(`/tenants/${tenantId}/holidays`, newLeave);
      await fetchData(); // Refresh list to get real calculated duration/status
      setShowModal(false);
      setNewLeave({ title: '', startDate: '', endDate: '', type: 'conge_annuel' });
    } catch (err) {
      alert('Failed to submit leave request: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleApprove = async (id) => {
    try {
      const res = await api.put(`/tenants/${tenantId}/holidays/${id}/approve`);
      if (res.data?.data) {
        setHolidays(prev => prev.map(h => h.id === id ? { ...h, status: res.data.data.status } : h));
      }
      fetchData();
    } catch (err) {
      alert('Approval failed: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleReject = async (id) => {
    const reason = prompt('Reason for rejection (optional):');
    if (reason === null) return; // Cancelled
    try {
      const res = await api.put(`/tenants/${tenantId}/holidays/${id}/reject`, { reason });
      if (res.data?.data) {
        setHolidays(prev => prev.map(h => h.id === id ? { ...h, status: res.data.data.status } : h));
      }
      fetchData();
    } catch (err) {
      alert('Rejection failed: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this leave request?')) return;
    try {
      const res = await api.put(`/tenants/${tenantId}/holidays/${id}/cancel`);
      if (res.data?.data) {
        setHolidays(prev => prev.map(h => h.id === id ? { ...h, status: res.data.data.status } : h));
      }
      fetchData();
    } catch (err) {
      alert('Cancellation failed: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  return (
    <div className="app-container">
      {/* Sidebar Navigation */}
      <aside className="sidebar">
        <div>
          <div className="brand-header">
            <div className="brand-logo">P</div>
            <div>
              <h2 className="brand-title">Prodexly Holidays</h2>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Moroccan HR SaaS</span>
            </div>
          </div>

          <ul className="nav-list">
            <li className={`nav-item ${activeMenu === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveMenu('dashboard')}>
              <Calendar size={18} />
              <span>Leave Dashboard</span>
            </li>
            
            {/* Only Manager and HR Admin can see the Staff Directory */}
            {user && (user.role === 'manager' || user.role === 'hr_admin' || user.role === 'owner') && (
              <li className={`nav-item ${activeMenu === 'staff' ? 'active' : ''}`} onClick={() => setActiveMenu('staff')}>
                <Users size={18} />
                <span>Staff & Roles</span>
              </li>
            )}
            
            <li className={`nav-item ${activeMenu === 'policies' ? 'active' : ''}`} onClick={() => setActiveMenu('policies')}>
              <Layers size={18} />
              <span>Tenant Policies</span>
            </li>
            <li className="nav-item">
              <ShieldCheck size={18} />
              <span>Audit & Logs</span>
            </li>
          </ul>
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, color: 'var(--primary-400)' }}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontWeight: 500, fontSize: '0.9rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{user?.name}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{user?.role?.replace('_', ' ')}</div>
            </div>
          </div>
          <button className="btn btn-outline" style={{ width: '100%', padding: '8px', fontSize: '0.85rem' }} onClick={logout}>
            Log Out
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <main className="main-content">
        {/* Top Header Bar */}
        <header className="header-bar">
          <div>
            <h1>{activeMenu === 'dashboard' ? 'Holiday & Leave Management' : 'Staff Directory'}</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              Compliant with Code du Travail Marocain (1.5 days/month)
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="tenant-badge">
              <div className="pulse-dot"></div>
              <span>Tenant: {tenantId}</span>
            </div>

            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              <Plus size={18} />
              <span>New Leave Request</span>
            </button>
          </div>
        </header>

        {/* Dynamic Content */}
        {activeMenu === 'dashboard' && (
          <>
            <div className="tabs" style={{ marginBottom: '24px' }}>
              <button className={`tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>All Requests</button>
              <button className={`tab ${activeTab === 'pending_manager' ? 'active' : ''}`} onClick={() => setActiveTab('pending_manager')}>Pending N+1</button>
              <button className={`tab ${activeTab === 'pending_hr' ? 'active' : ''}`} onClick={() => setActiveTab('pending_hr')}>Pending HR</button>
              <button className={`tab ${activeTab === 'approved' ? 'active' : ''}`} onClick={() => setActiveTab('approved')}>Approved</button>
            </div>
            
            <LeaveDashboard 
              holidays={holidays} 
              activeTab={activeTab}
              setShowModal={setShowModal}
              handleApprove={handleApprove}
              handleReject={handleReject}
              handleCancel={handleCancel}
              currentUserRole={user?.role}
              currentUserId={user?.userId}
              currentUser={user}
            />
          </>
        )}
        
        {activeMenu === 'staff' && (
          <StaffList tenantId={tenantId} />
        )}

        {activeMenu === 'policies' && (
          <TenantPolicies />
        )}
      </main>

      {/* Leave Request Modal */}
      <LeaveModal 
        showModal={showModal}
        setShowModal={setShowModal}
        newLeave={newLeave}
        setNewLeave={setNewLeave}
        handleCreateLeave={handleCreateLeave}
      />
    </div>
  );
}
